import { Cell, Label, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from 'recharts';
import { FC, ReactNode, memo, useState } from 'react';
import { HelpCircle } from 'lucide-react';

import { CHART_SERIES_COLORS, chartContrastStroke } from 'features/common/utils/chartColors';
import { common } from 'theme';
import styles from './PunchupDonutChart.module.css';

export interface DonutSlice {
	/** Slice label shown in tooltip and legend. */
	label: string;
	/** Numeric value for this slice. */
	value: number;
	/** Fill color for this slice. Use CHART_SERIES_COLORS[i] for consistent palette. */
	color: string;
}

/**
 * A fixed legend entry that is always rendered regardless of slice values.
 * When `colors` is provided the swatch renders as a multi-dot strip
 * (e.g. "Issued" on the summary donut groups several ticket type colours).
 */
export interface LegendItem {
	label: string;
	color?: string;
	colors?: string[];
}

export interface DonutStatRow {
	/** Per-slice label (e.g. ticket type name). */
	label: string;
	/** Pre-formatted derived value (e.g. "$45 avg"). */
	value: string;
}

export interface PunchupDonutChartProps {
	/**
	 * Slice definitions. Order determines rendering sequence (clockwise from top).
	 * At least two slices are needed for a meaningful donut — a single non-zero
	 * slice renders as a full ring.
	 */
	data: DonutSlice[];
	/** Card header title (e.g. "All Tickets", "VIP"). */
	title?: string;
	/**
	 * Optional sub-title below the main title (e.g. ticket type name for small
	 * per-type donuts in the inventory grid).
	 */
	subtitle?: string;
	/**
	 * Text rendered in the center of the donut hole.
	 * Use for the key summary metric (e.g. "42/100" for sold/total).
	 */
	centerLabel?: string;
	/**
	 * Fill colour for the center label. Defaults to the muted greyBlue. Pass a warning/danger colour
	 * to flag an exceptional value (e.g. an over-subscribed pool whose issued count exceeds capacity).
	 */
	centerLabelColor?: string;
	/** Chart area size in px (width = height = this value). Defaults to 180. */
	size?: number;
	/**
	 * Optional compact stats table rendered below the chart.
	 * Use for per-slice derived values (e.g. avg revenue per ticket type).
	 * Each row: label (slice name) + pre-formatted value.
	 */
	stats?: DonutStatRow[];
	/**
	 * When provided, a HelpCircle icon appears in the bottom-right corner of the card.
	 * On hover it shows this text as a tooltip explaining the metric, its formula,
	 * and what a high or low value means.
	 * @deprecated Use helpButton with PunchupHelpButton for rich 3-segment tooltips.
	 */
	helpText?: string;
	/**
	 * Replaces the CSS-only helpText tooltip with a portal-based PunchupHelpButton.
	 * Pass <PunchupHelpButton mainText="..." codeText="..." secondaryText="..." />.
	 * Takes precedence over helpText when both are provided.
	 */
	helpButton?: ReactNode;
	/**
	 * When true, renders a compact color-swatch + label legend below the chart area.
	 * Only slices with value > 0 appear in the legend. Use on donuts where segment
	 * identity is not otherwise obvious (e.g. multi-segment inventory issued donuts).
	 */
	showLegend?: boolean;
	/**
	 * Fixed legend entries rendered instead of the auto-derived slice legend.
	 * All items are always shown (even if value is 0), making this suitable for
	 * summary donuts that need a stable 4-entry key (Available / Issued / Pooled / Comped).
	 * Takes precedence over showLegend when both are provided.
	 */
	legendItems?: LegendItem[];
	/**
	 * @deprecated No longer used. The legend always renders as a two-column
	 * grid (swatch column | label column) via display:contents on each item.
	 * Kept in the interface to avoid breaking existing call sites.
	 */
	legendColumns?: number;
	/**
	 * Custom formatter for the Recharts tooltip value.
	 * Receives `(value, name)` and returns `[formattedValue, label]`.
	 * When omitted the raw numeric value is shown.
	 * Use for revenue rings where `value` is a dollar amount and should be formatted as currency.
	 * Note: Recharts types the return narrowly as `[number, string]`; the `as any` cast on the
	 * Tooltip prop is intentional and required to allow formatted string values.
	 */
	tooltipFormatter?: (value: number, name: string) => [string, string];
	/**
	 * When provided, hovering the donut center hole shows this text as a tooltip.
	 * Use for a full-sentence explanation of the center metric
	 * (e.g. "Issued 42 out of 100 total tickets" or "Sold $1.2k of $5.0k ceiling").
	 */
	centerTooltip?: string;
}

const PunchupDonutChartBase: FC<PunchupDonutChartProps> = ({
	data,
	title,
	subtitle,
	centerLabel,
	centerLabelColor,
	size = 180,
	stats,
	helpText,
	helpButton,
	showLegend,
	legendItems,
	tooltipFormatter,
	centerTooltip,
}) => {
	const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
	const [centerHovered, setCenterHovered] = useState(false);
	const hasHeader = !!(title || subtitle);
	const helpSlot =
		helpButton ??
		(helpText ? (
			<span className={styles.helpIconBtn} aria-label="Help">
				<HelpCircle size={12} />
				<span role="tooltip" className={styles.helpTooltip}>
					{helpText}
				</span>
			</span>
		) : null);

	return (
		<div className={styles.card}>
			{helpSlot}
			{hasHeader && (
				<div className={styles.cardHeader}>
					{title && <span className={styles.title}>{title}</span>}
					{subtitle && <span className={styles.subtitle}>{subtitle}</span>}
				</div>
			)}
			<div className={styles.chartArea}>
				{/* position:relative wrapper so the center overlay can be absolutely positioned */}
				<div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
					<ResponsiveContainer width={size} height={size}>
						<PieChart>
							<Pie
								data={data}
								dataKey="value"
								nameKey="label"
								innerRadius="62%"
								outerRadius="87%"
								startAngle={90}
								endAngle={-270}
								strokeWidth={1}
								activeIndex={activeIndex}
								// Recharts' activeShape callback props are internal/untyped — same boundary
								// as the legend and tooltip formatter callbacks below.
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								activeShape={(props: any) => {
									const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
									return (
										<Sector
											cx={cx}
											cy={cy}
											innerRadius={innerRadius}
											outerRadius={outerRadius + 4}
											startAngle={startAngle}
											endAngle={endAngle}
											fill={fill}
											stroke={chartContrastStroke(fill)}
											strokeWidth={1}
										/>
									);
								}}
								onMouseEnter={(_data, index) => setActiveIndex(index)}
								onMouseLeave={() => setActiveIndex(undefined)}
							>
								{/* Each slice carries a darker proportional outline so it reads crisply on the white
								    card and separates from its neighbour (see chartContrastStroke). */}
								{data.map((slice) => (
									<Cell key={slice.label} fill={slice.color} stroke={chartContrastStroke(slice.color)} />
								))}
								{centerLabel && (
									<Label
										position="center"
										content={({ viewBox }) => {
											const { cx = 0, cy = 0 } = viewBox as { cx?: number; cy?: number };
											return (
												<text
													x={cx}
													y={cy}
													textAnchor="middle"
													dominantBaseline="middle"
													fontFamily={common.fonts.primary}
													fontSize={size >= 160 ? 14 : 11}
													fontWeight={600}
													fill={centerLabelColor ?? common.colors.greyBlue}
												>
													{centerLabel}
												</text>
											);
										}}
									/>
								)}
							</Pie>
							<Tooltip
								// Recharts Formatter<number,string> narrowly types the return to [number,string],
								// but we need to return a formatted string for revenue rings. Cast required.
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								formatter={(tooltipFormatter ?? ((value: number, name: string) => [value, name])) as any}
								contentStyle={{
									fontFamily: common.fonts.primary,
									fontSize: 12,
									borderRadius: 6,
									borderColor: common.colors.grey19,
								}}
								// Elevate above neighboring chart cards (each has position:relative creating
								// a stacking context). Without this, long tooltips render below adjacent rows.
								wrapperStyle={{ zIndex: 9999 }}
							/>
						</PieChart>
					</ResponsiveContainer>
					{centerTooltip && (
						<div
							className={styles.centerTip}
							tabIndex={0}
							role="button"
							aria-label={centerTooltip}
							onMouseEnter={() => setCenterHovered(true)}
							onMouseLeave={() => setCenterHovered(false)}
							onFocus={() => setCenterHovered(true)}
							onBlur={() => setCenterHovered(false)}
						>
							{centerHovered && <div className={styles.centerTooltip}>{centerTooltip}</div>}
						</div>
					)}
				</div>
			</div>
			{(legendItems || (showLegend && data.filter((s) => s.value > 0).length > 0)) && (
				<div className={styles.legend}>
					{legendItems
						? legendItems.map((item) => (
								<div key={item.label} className={styles.legendItem}>
									{item.colors && item.colors.length > 0 ? (
										<span className={styles.legendSwatches}>
											{item.colors.slice(0, 5).map((c, i) => (
												<span
													key={i}
													className={styles.legendSwatch}
													style={{ background: c, borderColor: chartContrastStroke(c) }}
												/>
											))}
										</span>
									) : (
										<span
											className={styles.legendSwatch}
											style={{
												background: item.color,
												borderColor: item.color ? chartContrastStroke(item.color) : undefined,
											}}
										/>
									)}
									<span className={styles.legendLabel}>{item.label}</span>
								</div>
						  ))
						: data
								.filter((s) => s.value > 0)
								.map((slice) => (
									<div key={slice.label} className={styles.legendItem}>
										<span
											className={styles.legendSwatch}
											style={{ background: slice.color, borderColor: chartContrastStroke(slice.color) }}
										/>
										<span className={styles.legendLabel}>{slice.label}</span>
									</div>
								))}
				</div>
			)}
			{stats && stats.length > 0 && (
				<div className={styles.statsTable}>
					{stats.map((row) => (
						<div key={row.label} className={styles.statsRow}>
							<span className={styles.statsLabel}>{row.label}</span>
							<span className={styles.statsValue}>{row.value}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export const PunchupDonutChart = memo(PunchupDonutChartBase);
PunchupDonutChart.displayName = 'PunchupDonutChart';

export { CHART_SERIES_COLORS };
